import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';
import { Radio } from 'lucide-react';
import type { LiveSummary, Platform } from '@multimod/shared';
import { useMe, useLogout } from '@/features/auth/hooks';
import { useChatFeed } from '@/features/chat-feed/hooks';
import { ChatFeed } from '@/features/chat-feed/ChatFeed';
import { TopBar } from '@/features/panel/components/TopBar';
import { ChannelSummary } from '@/features/panel/components/ChannelSummary';
import { BroadcastComposer } from '@/features/panel/components/BroadcastComposer';
import { DiscoverySheet } from '@/features/panel/components/DiscoverySheet';
import { ShortcutsSheet } from '@/features/panel/components/ShortcutsSheet';
import { CommandPalette } from '@/features/panel/components/CommandPalette';
import { useDetachLive } from '@/features/panel/hooks/useDetachLive';
import { useGlobalHotkeys } from '@/features/panel/hooks/useGlobalHotkeys';
import { usePanelStore } from '@/features/panel/store/panel-store';
import { useActiveLives, useYoutubeLiveRefresh } from '@/features/live-discovery/hooks';
import { updateMissCounts, isStale } from '@/features/panel/lib/stale-lives';
import { ApiError } from '@/lib/api';
import { DevMockPanel } from '@/features/dev-mock/DevMockPanel';
import type { ChatFeedStore } from '@/features/chat-feed/store';

function PanelSession({
  selected,
  attached,
  onStoreReady,
}: {
  selected: LiveSummary;
  attached: LiveSummary[];
  onStoreReady?: (store: ChatFeedStore) => void;
}) {
  const { store, attach } = useChatFeed(selected.liveChatId, selected.platform);
  const { data: me } = useMe();

  useEffect(() => {
    onStoreReady?.(store);
  }, [store, onStoreReady]);

  // Injeta o externalId do próprio mod por plataforma no store. É o sinal que
  // permite reconciliar o eco da própria mensagem por identidade (em vez de por
  // id da plataforma ou janela de tempo), matando o duplo-render do broadcast
  // sem dropar mensagens que o mod manda pelo chat nativo. Ver store.ts `push`.
  useEffect(() => {
    if (!me) return;
    const ids: Partial<Record<Platform, string>> = {};
    const names: Partial<Record<Platform, string>> = {};
    for (const p of ['twitch', 'youtube'] as const) {
      const conn = me.connections[p];
      if (conn?.externalId) ids[p] = conn.externalId;
      // #81: nome real do mod por plataforma — alimenta o displayName da msg
      // otimista do broadcast (paridade com as msgs nativas dele).
      if (conn?.displayName) names[p] = conn.displayName;
    }
    store.getState().setSelfExternalIds(ids);
    store.getState().setSelfDisplayNames(names);
  }, [store, me]);

  useEffect(() => {
    for (const live of attached) {
      if (live.platform !== selected.platform) {
        attach({ platform: live.platform, externalId: live.login, liveChatId: live.liveChatId });
      }
    }
  }, [selected.liveChatId, selected.platform, attached, attach]);

  return (
    <>
      <main className="overflow-hidden">
        <ChatFeed store={store} />
      </main>
      <BroadcastComposer activeLives={[selected, ...attached]} store={store} />
    </>
  );
}

export function PanelPage() {
  const { data: me, isLoading } = useMe();
  const logout = useLogout();
  // selected + attachedLives vivem no panel-store pra sobreviver à navegação
  // entre /painel e /settings. Sem isso, o desmount do PanelPage zera o
  // contexto e o mod perde a live ao abrir Configurações.
  const selected = usePanelStore((s) => s.selectedLive);
  const attachedLives = usePanelStore((s) => s.attachedLives);
  const setSelected = usePanelStore((s) => s.setSelectedLive);
  const resetAttachedLives = usePanelStore((s) => s.resetAttachedLives);
  const addAttachedLive = usePanelStore((s) => s.addAttachedLive);
  const removeAttachedByPlatform = usePanelStore((s) => s.removeAttachedByPlatform);
  const patchLivesByLiveChatId = usePanelStore((s) => s.patchLivesByLiveChatId);
  // store é wrapped em ref-box porque ChatFeedStore é callable (Zustand UseBoundStore)
  // e React useState interpreta funções como updaters lazy.
  const [mockStoreBox, setMockStoreBox] = useState<{ s: ChatFeedStore } | null>(null);
  const mockStore = mockStoreBox?.s ?? null;
  const handleStoreReady = useCallback((s: ChatFeedStore) => {
    setMockStoreBox((cur) => (cur?.s === s ? cur : { s }));
  }, []);
  const open = usePanelStore((s) => s.discoverySheetOpen);
  const setOpen = usePanelStore((s) => s.setDiscoverySheetOpen);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { data: refreshedLives } = useActiveLives();

  useGlobalHotkeys({
    '?': () => setShortcutsOpen(true),
    'mod+k': () => setPaletteOpen((cur) => !cur),
  });

  // useActiveLives já refetcha a cada 30s. Merge dos campos voláteis (viewerCount,
  // title) preserva liveChatId/identidade — só atualiza o que muda no tempo.
  // Lives que sumiram da resposta (encerradas) mantêm o último valor conhecido.
  const refreshedPatches = useMemo(() => {
    if (!refreshedLives) return null;
    return new Map(
      refreshedLives.map((l) => [l.liveChatId, { viewerCount: l.viewerCount, title: l.title }]),
    );
  }, [refreshedLives]);
  useEffect(() => {
    if (refreshedPatches) patchLivesByLiveChatId(refreshedPatches);
  }, [refreshedPatches, patchLivesByLiveChatId]);

  // #144: /me/streams só traz Twitch (YouTube não lista lives de canal moderado).
  // Pra não deixar o viewerCount de uma live YouTube anexada congelado, repollamos
  // /youtube/resolve pelo videoId que viaja no LiveSummary.
  const youtubeVideoId = useMemo(() => {
    if (selected?.platform === 'youtube') return selected.videoId ?? null;
    return attachedLives.find((l) => l.platform === 'youtube')?.videoId ?? null;
  }, [selected, attachedLives]);
  const { data: refreshedYoutube, error: youtubeRefreshError } =
    useYoutubeLiveRefresh(youtubeVideoId);
  useEffect(() => {
    if (!refreshedYoutube) return;
    patchLivesByLiveChatId(
      new Map([
        [
          refreshedYoutube.liveChatId,
          { viewerCount: refreshedYoutube.viewerCount, title: refreshedYoutube.title },
        ],
      ]),
    );
  }, [refreshedYoutube, patchLivesByLiveChatId]);

  // #147: detecção de live encerrada. Twitch — conta ciclos ausente em /me/streams;
  // YouTube — um 404 not_live no re-resolve. Lives stale mantêm o último valor mas
  // ganham badge "encerrada" em vez de fingir que seguem ativas.
  const [missCounts, setMissCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!refreshedLives) return;
    const present = new Set(refreshedLives.map((l) => l.liveChatId));
    const trackedTwitch = [selected, ...attachedLives]
      .filter((l): l is LiveSummary => !!l && l.platform === 'twitch')
      .map((l) => l.liveChatId);
    setMissCounts((prev) => updateMissCounts(prev, trackedTwitch, present));
  }, [refreshedLives, selected, attachedLives]);
  // YouTube encerrou quando o re-resolve responde 404 (not_live). Erros transitórios
  // (502/network) não marcam stale — só o 404 definitivo.
  const youtubeEnded =
    youtubeRefreshError instanceof ApiError && youtubeRefreshError.status === 404;

  // #131/#156: optimistic remove com revert por live. Cada mutation captura a
  // live que removeu e a devolve via context (em useDetachLive) — sem snapshot
  // compartilhado, detaches concorrentes não corrompem o revert um do outro.
  const detach = useDetachLive({
    onOptimisticRemove: (platform) => {
      const removed = attachedLives.find((l) => l.platform === platform) ?? null;
      removeAttachedByPlatform(platform);
      return removed;
    },
    onRevert: (_platform, removed) => {
      if (removed) addAttachedLive(removed);
    },
  });

  // Não auto-abre o sheet (issue #97). Empty state da main área mostra CTA
  // explícito; abrir só por ação do mod evita feel "modal-spam" no load.

  // Auth básico vem do <RequireAuth> em routes.tsx. Aqui cobrimos só as
  // regras de domínio específicas do /painel: precisa de twitch conectado
  // com scopes válidos.
  if (isLoading || !me) return null;
  if (!me.connections.twitch) return <Navigate to="/auth/connect" replace />;
  if (me.connections.twitch.missingScopes.length > 0) return <Navigate to="/auth/connect?reauth=1" replace />;

  const slotByPlatform = (p: Platform): LiveSummary | null => {
    if (selected?.platform === p) return selected;
    return attachedLives.find((l) => l.platform === p) ?? null;
  };

  // Detach handler por plataforma — só disponibilizado pra plataforma que está
  // anexada (não-primary). UI bloqueia o botão pra primary; aqui defendemos
  // contra clique programático/race com primary swap.
  const detachAttached = (platform: Platform) => {
    if (!selected || selected.platform === platform) return;
    detach.mutate({
      detachPlatform: platform,
      primaryPlatform: selected.platform,
      primaryLiveChatId: selected.liveChatId,
    });
  };

  // `onDetach` é definido por plataforma só quando:
  //   1. Há live primary (`selected`)
  //   2. A plataforma em questão NÃO é a primary
  //   3. A plataforma tem uma live ativa (else nada pra desanexar)
  const onDetachFor = (platform: Platform): (() => void) | undefined => {
    if (!selected || selected.platform === platform) return undefined;
    if (!slotByPlatform(platform)) return undefined;
    return () => detachAttached(platform);
  };

  return (
    <div className="h-screen overflow-hidden grid bg-bg" style={{ gridTemplateRows: '56px 76px 1fr auto' }}>
      <TopBar
        selected={selected}
        twitch={slotByPlatform('twitch')}
        youtube={slotByPlatform('youtube')}
        store={mockStore}
        onOpenDiscovery={() => setOpen(true)}
        onOpenShortcuts={() => setShortcutsOpen(true)}
        onOpenPalette={() => setPaletteOpen(true)}
        onLogout={() => logout.mutate()}
      />
      <ChannelSummary
        twitch={{
          live: slotByPlatform('twitch'),
          connection: me.connections.twitch
            ? {
                displayName: me.connections.twitch.displayName,
                externalId: me.connections.twitch.externalId,
              }
            : null,
          stale: (() => {
            const l = slotByPlatform('twitch');
            return !!l && isStale(missCounts[l.liveChatId]);
          })(),
          ...(onDetachFor('twitch') ? { onDetach: onDetachFor('twitch')! } : {}),
        }}
        youtube={{
          live: slotByPlatform('youtube'),
          connection: me.connections.youtube
            ? {
                displayName: me.connections.youtube.displayName,
                externalId: me.connections.youtube.externalId,
              }
            : null,
          stale: !!slotByPlatform('youtube') && youtubeEnded,
          ...(onDetachFor('youtube') ? { onDetach: onDetachFor('youtube')! } : {}),
        }}
        store={mockStore}
      />
      {selected ? (
        <PanelSession selected={selected} attached={attachedLives} onStoreReady={handleStoreReady} />
      ) : (
        <>
          <main className="overflow-hidden">
            <div className="h-full grid place-items-center px-6">
              <div className="flex flex-col items-center gap-3 text-center max-w-sm">
                <Radio size={40} strokeWidth={1.5} className="text-fg-subtle" aria-hidden="true" />
                <p className="text-sm text-fg-muted">
                  Nenhuma live ativa no painel. Selecione uma live no botão{' '}
                  <strong className="text-fg font-medium">Conectar live</strong> no topo.
                </p>
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="mt-1 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-primary text-primary-fg text-sm font-medium hover:bg-primary/90 transition-colors duration-fast"
                >
                  Conectar live
                </button>
              </div>
            </div>
          </main>
          <BroadcastComposer />
        </>
      )}

      <ShortcutsSheet open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        store={mockStore}
        onOpenShortcuts={() => setShortcutsOpen(true)}
      />

      <DiscoverySheet
        open={open}
        onOpenChange={setOpen}
        selected={selected}
        onSelectPrimary={(live) => {
          setSelected(live);
          // Se selecionar uma secondary já anexada como primary, mantém o array;
          // se for nova, garante presença no array pra ActiveFeedBridge enxergar.
          addAttachedLive(live);
        }}
        attachedLives={attachedLives}
        onAddAttached={addAttachedLive}
        store={mockStore}
      />

      <DevMockPanel
        store={mockStore}
        selectedLive={selected}
        onAttachMockLive={(live, attached) => {
          setSelected(live);
          resetAttachedLives(attached);
          setOpen(false);
        }}
      />
    </div>
  );
}
